import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Button,
  Checkbox,
  Chip,
  TextField,
} from '@mui/material'
import {
  AddShoppingCart,
  Alarm,
  Check,
  CheckBox,
  CheckBoxOutlineBlank,
  Close,
  Inbox,
  InfoOutlined,
  MoneyOff,
  ReceiptOutlined,
} from '@mui/icons-material'
import { EOCStatusOptions } from 'src/utils/EOCStatus'
import QuoteTypeNameConstants from 'src/constants/QuoteTypeNameConstants'
import { inject, observer } from 'mobx-react'
import React from 'react'
import ReactPaginate from 'react-paginate'
import { pagedList } from 'src/viewModels/pagedList'
import { IStores } from 'src/Stores'
import { IUser } from 'src/Definitions'
import { SearchRequestBase } from 'src/viewModels/SearchRequestBase'
import { Helmet } from 'react-helmet'
import ResponsiveAppBar from 'src/common/ResponsiveAppBar'
import {
  CommentOutline,
  Thermometer,
  ThermometerHigh,
  ThermometerLow,
} from 'mdi-material-ui'
import { IncomingDocumentSummary } from 'src/viewModels/IncomingDocumentSummary'
import {
  getAllBulkActions,
  getAllInboxItems,
  getAllIncomingDocuments,
  getInboxesByUser,
  getInboxTypes,
  updateInboxFavorites,
} from 'src/services/WorkflowService'
import moment from 'moment'
import { InboxSummary } from 'src/viewModels/workflow/InboxSummary'
import { DocumentInboxItemSummary } from 'src/viewModels/workflow/DocumentInboxItemSummary'
import { RouterStore } from 'mobx-react-router'
import { ClaimInboxItemSummary } from 'src/viewModels/workflow/ClaimInboxItemSummary'
import { formatCurrency, toISODate } from 'src/utils/Formatter'
import { InboxItemType, InboxTypeItemLabels } from 'src/utils/InboxItemType'
import { InvoiceCustomForm } from '../utils/InvoiceCustomForm'
import { InvoiceInboxItemSummary } from '../viewModels/workflow/InvoiceInboxItemSummary'
import BulkAssignToEntryUserAndSendModal from './customForms/Bulk/BulkAssignToEntryUserAndSendModal'
import SortedTableCell, { sortedTableHeaderSx } from './SortedTableCell'
import { QuoteInboxItemSummary } from 'src/viewModels/workflow/QuoteInboxItemSummary'
import InvoiceStatusIcon from 'src/quotes/InvoiceStatusIcon'
import BundleClaimsModal from 'src/quotes/QuoteBills/BundleClaimsModal'
import { BilloutInboxItemSummary } from 'src/viewModels/workflow/BilloutInboxItemSummary'
import { InboxActionSummary } from 'src/viewModels/workflow/InboxActionSummary'
import BulkStandardCommentModal from './customForms/Bulk/BulkStandardCommentModal'
import { OrganizationId } from 'src/utils/OrganizationIds'
import BulkAssignPendModal from './customForms/Bulk/BulkAssignPendModel'
import BulkGenerateInvoiceEmailModal from './customForms/Bulk/BulkGenerateInvoiceEmailModal'
import { BulkActionItem } from 'src/viewModels/workflow/BulkActionItem'
import { StaticAction } from 'src/utils/StaticAction'
import { CaseInboxItemSummary } from 'src/viewModels/workflow/CaseInboxItemSummary'
import { FixedInboxSearchRequest } from 'src/viewModels/workflow/FixedInboxFilterRequest'
import {
  ProviderTypeAbbreviation,
  ProviderTypeLabels,
} from 'src/utils/ProviderTypeAbbreviation'
import {
  ProviderGroupPaymentTier,
  ProviderGroupPaymentTierLabels,
} from 'src/utils/ProviderGroupPaymentTier'
import AppealNameConstants from 'src/constants/AppealNameConstants'
import AppealLogModal from 'src/search/AppealLogModal'
import { InboxTypeSummary } from 'src/viewModels/workflow/InboxTypeSummary'
import { WorkflowInboxUserFavorite } from 'src/viewModels/workflow/WorkflowInboxUserFavorite'

const compactButtonSx = { py: 0.25, px: 1, minWidth: 0, lineHeight: 1.5 }

const compactFieldSx = {
  '& .MuiInputBase-root': { height: 34, fontSize: '0.85rem' },
  '& .MuiInputBase-input, & .MuiNativeSelect-select, & .MuiSelect-select': {
    paddingTop: '6px',
    paddingBottom: '6px',
  },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
  '& .MuiInputLabel-shrink': {
    fontSize: '0.75rem',
    transform: 'translate(14px, -8px) scale(1)',
  },
  '& .MuiOutlinedInput-notchedOutline legend': { fontSize: '0.75rem' },
}

interface IInboxesProps {
  routerStore?: RouterStore
  editBill?: (eocId: string) => void
  isLoading?: boolean
  setClaimsModalLoadFunction?: (
    parentFunction?: (episodeOfCareId: string) => void
  ) => void
  patientInfo?: string
  scheduleId?: string
  rerouteToPath?: (path: string) => void
  setSelectedInbox?: (inbox: InboxSummary | undefined) => void
  savedInbox?: InboxSummary | undefined
  savedOrder?: string | undefined
  savedOrderBy?: string | undefined
  setSavedSort?: (order: string | undefined, orderBy: string | undefined) => void
  openBillsDialog?: (quoteId: string) => Promise<void>
  savedInboxType?: InboxItemType | undefined
  setSelectedInboxType?: (type: InboxItemType | undefined) => void
  organizationId?: OrganizationId
  savedFixedInboxFilter?: FixedInboxSearchRequest
  setFixedImportRequest?: (fixedInboxSearchRequest: FixedInboxSearchRequest) => void
  user?: IUser
  openAppealLogModal?: (claimId: string) => void
  closeAppealLogModal?: () => void
  isAppealLogModalOpen?: boolean
  selectedClaimId?: string
}

type BulkModalRenderProps = {
  onClose: (actionsExecuted: boolean) => void
  isOpen: boolean
  inboxItems: Array<BulkActionItem>
  inboxId: string
  action: InboxActionSummary
  itemType?: InboxItemType
  userName?: string
}

type BulkModalName =
  | 'assignPendModal'
  | 'commentModal'
  | 'assignAndSendModal'
  | 'generateInvoiceEmailModal'

const bulkModalRenderers: Record<
  BulkModalName,
  (props: BulkModalRenderProps) => React.ReactElement | null
> = {
  assignPendModal: (props) => <BulkAssignPendModal {...props} />,
  commentModal: (props) => <BulkStandardCommentModal {...props} />,
  assignAndSendModal: (props) => <BulkAssignToEntryUserAndSendModal {...props} />,
  generateInvoiceEmailModal: (props) => <BulkGenerateInvoiceEmailModal {...props} />,
}

class Inboxes extends React.Component<IInboxesProps> {
  private alphabetSort = (a: InboxSummary, b: InboxSummary) => {
    if (b.inboxName > a.inboxName) return -1
    if (b.inboxName < a.inboxName) return 1
    return 0
  }

  public state = {
    dynamicColumns: new Array<string>(),
    gridData: new pagedList().items as
      | DocumentInboxItemSummary[]
      | IncomingDocumentSummary[]
      | ClaimInboxItemSummary[]
      | QuoteInboxItemSummary[]
      | BilloutInboxItemSummary[]
      | InvoiceInboxItemSummary[]
      | { [key: string]: any }[],

    page: 1,
    pageCount: 0,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    pageSize: 25,
    selectedItemType: '',
    inbox: undefined as unknown as InboxSummary,
    gridLoading: true,
    inboxes: new Array<InboxSummary>(),
    inboxTypes: new Array<InboxTypeSummary>(),
    order: 'asc',
    orderBy: '',
    selectedType: undefined,
    bulkActions: new Array<InboxActionSummary>(),
    selectedBulkAction: undefined,
    actionToExecute: undefined,
    checkInboxItems: new Array<BulkActionItem>(),
    activeModal: null as unknown as BulkModalName,
    executed: false,
    filterClaimProviderGroup:
      this.props.savedFixedInboxFilter?.providerGroup ?? undefined,
    filterClaimProviderTypeAbbreviation:
      this.props.savedFixedInboxFilter?.providerTypeAbbreviation ?? undefined,
    filterQuoteReconcileUrgency:
      this.props.savedFixedInboxFilter?.providerGroupPaymentTier ?? undefined,
    filterQuoteInsuranceCompany:
      this.props.savedFixedInboxFilter?.insuranceCompany ?? undefined,
    filterQuoteInvoiceStatus: this.props.savedFixedInboxFilter?.hasInvoice
      ? 'Invoiced'
      : this.props.savedFixedInboxFilter?.notInvoiced
        ? 'NotInvoiced'
        : '',
    filterQuoteEocStatus: this.props.savedFixedInboxFilter?.eocStatus ?? '',
    filterQuoteTypes: (this.props.savedFixedInboxFilter?.quoteTypes ?? []).map(String),
    groupInfo: '' as string,
    favoriteModalOpen: false,
    favoriteInboxes: new Array<InboxSummary>(),
    favoriteSaving: false,
  }

  private getAvailableInboxTypes = (inboxTypes: InboxTypeSummary[]) => {
    const itemTypes = new Set<string>()

    return inboxTypes.filter((inboxType) => {
      const itemType = inboxType.id.toString() as InboxItemType

      if (
        this.props.organizationId === OrganizationId.Medical &&
        itemType !== InboxItemType.Case
      ) {
        return false
      }

      if (
        this.props.organizationId !== OrganizationId.Medical &&
        itemType === InboxItemType.Case
      ) {
        return false
      }

      if (itemTypes.has(itemType)) {
        return false
      }

      itemTypes.add(itemType)
      return true
    })
  }

  private availableInboxTypes = () => this.getAvailableInboxTypes(this.state.inboxTypes)

  private loadInboxTypes = async () => {
    const inboxTypes = await getInboxTypes()
    const selectedType = this.getInitialSelectedInboxType(inboxTypes)

    this.setState(
      {
        inboxTypes,
        selectedType,
        gridLoading: selectedType != undefined,
      },
      async () => {
        if (selectedType != undefined) {
          this.props.setSelectedInboxType!(selectedType)
          await this.loadInboxes()
        }
      }
    )
  }

  private getInitialSelectedInboxType = (
    inboxTypes: InboxTypeSummary[]
  ): InboxItemType | undefined => {
    const availableTypes = this.getAvailableInboxTypes(inboxTypes).map(
      (inboxType) => inboxType.id.toString() as InboxItemType
    )

    if (
      this.props.savedInboxType != undefined &&
      availableTypes.includes(this.props.savedInboxType)
    ) {
      return this.props.savedInboxType
    }

    return availableTypes.length === 1 ? availableTypes[0] : undefined
  }
  private async executeBulkAction() {
    let selectedAction = this.state.actionToExecute as unknown as InboxActionSummary

    if (
      selectedAction?.staticActionId != undefined &&
      selectedAction?.staticActionId != StaticAction.AUTO_ASSIGN &&
      selectedAction?.customFormName == null
    ) {
      switch (selectedAction.staticActionId) {
        case StaticAction.RELEASE_LOCK:
          this.setState({
            activeModal: 'commentModal',
          })
          break
        case StaticAction.ASSIGN:
        case StaticAction.PEND:
          this.setState({
            activeModal: 'assignPendModal',
          })
          break
        default:
          break
      }
    } else if (
      selectedAction.customFormName == InvoiceCustomForm.AssignToEntryUserAndSend
    ) {
      this.setState({
        activeModal: 'assignAndSendModal',
      })
    } else if (
      selectedAction.customFormName == 'GenerateInvoiceEmail' ||
      selectedAction.customFormName == InvoiceCustomForm.GenerateInvoiceEmailModal
    ) {
      this.setState({
        activeModal: 'generateInvoiceEmailModal',
      })
    } else {
      this.setState({
        activeModal: 'commentModal',
      })
    }
  }

  private loadInboxes = async () => {
    const line = new InboxSummary()
    line.inboxType = 'line'
    line.inboxName = '─────────────────────────'
    const inboxes = await getInboxesByUser(this.state.selectedType)
    const incomingBox = inboxes.filter((x) => x.id == -1)
    const personal = inboxes
      .filter((x) => x.inboxType == 'DynamicPersonal')
      .sort(this.alphabetSort)
    const docInboxes = inboxes.filter(
      (x) => x.itemType == InboxItemType.Document && x.inboxType != 'DynamicPersonal'
    )
    const claimInboxes = inboxes.filter(
      (x) => x.itemType == InboxItemType.Claim && x.inboxType != 'DynamicPersonal'
    )
    const quoteInboxes = inboxes.filter(
      (x) => x.itemType == InboxItemType.Quote && x.inboxType != 'DynamicPersonal'
    )
    const billoutInboxes = inboxes.filter(
      (x) => x.itemType == InboxItemType.Billout && x.inboxType != 'DynamicPersonal'
    )
    const caseInboxes = inboxes.filter(
      (x) => x.itemType == InboxItemType.Case && x.inboxType != 'DynamicPersonal'
    )
    const invoiceInboxes = inboxes.filter(
      (x) => x.itemType == InboxItemType.Invoice && x.inboxType != 'DynamicPersonal'
    )

    let joinedInboxes = new Array<InboxSummary>()

    if (
      personal.length > 0 &&
      (docInboxes.length > 0 ||
        claimInboxes.length > 0 ||
        incomingBox.length > 0 ||
        quoteInboxes.length > 0 ||
        billoutInboxes.length > 0 ||
        caseInboxes.length > 0 ||
        invoiceInboxes.length > 0)
    ) {
      joinedInboxes = personal.concat(line)
    }

    if (incomingBox.length > 0) {
      joinedInboxes = joinedInboxes.concat(incomingBox)

      if (
        docInboxes.length > 0 ||
        claimInboxes.length > 0 ||
        quoteInboxes.length > 0 ||
        billoutInboxes.length > 0 ||
        caseInboxes.length > 0 ||
        invoiceInboxes.length > 0
      ) {
        joinedInboxes = joinedInboxes.concat(line)
      }
    }

    if (docInboxes.length > 0 || claimInboxes.length > 0) {
      if (docInboxes.length > 0 && claimInboxes.length > 0) {
        joinedInboxes = joinedInboxes.concat(docInboxes).concat(line).concat(claimInboxes)
      } else if (docInboxes.length > 0) {
        joinedInboxes = joinedInboxes.concat(docInboxes)
      } else {
        joinedInboxes = joinedInboxes.concat(claimInboxes)
      }

      if (quoteInboxes.length > 0) {
        joinedInboxes = joinedInboxes.concat(line)
      }
    }

    if (quoteInboxes.length > 0) {
      joinedInboxes = joinedInboxes.concat(quoteInboxes)
    }

    if (invoiceInboxes.length > 0) {
      joinedInboxes = joinedInboxes.concat(invoiceInboxes)
    }

    if (billoutInboxes.length > 0) {
      if (docInboxes.length > 0 || claimInboxes.length > 0 || quoteInboxes.length > 0) {
        joinedInboxes = joinedInboxes.concat(line)
      }
      joinedInboxes = joinedInboxes.concat(billoutInboxes)
    }

    if (caseInboxes.length > 0) {
      if (
        docInboxes.length > 0 ||
        claimInboxes.length > 0 ||
        quoteInboxes.length > 0 ||
        billoutInboxes.length > 0 ||
        invoiceInboxes.length > 0
      ) {
        joinedInboxes = joinedInboxes.concat(line)
      }
      joinedInboxes = joinedInboxes.concat(caseInboxes)
    }

    this.setState(
      {
        inboxes: joinedInboxes,
        inbox: this.props.savedInbox,
        selectedItemType: this.props.savedInbox?.itemType,
        gridLoading: this.props.savedInbox != undefined,
        order: this.props.savedOrder ?? 'asc',
        orderBy: this.props.savedOrderBy ?? '',
        filterClaimProviderGroup:
          this.props.savedFixedInboxFilter?.providerGroup ?? undefined,
        filterClaimProviderTypeAbbreviation:
          this.props.savedFixedInboxFilter?.providerTypeAbbreviation ?? undefined,
        filterQuoteReconcileUrgency:
          this.props.savedFixedInboxFilter?.providerGroupPaymentTier ?? undefined,
        filterQuoteInsuranceCompany:
          this.props.savedFixedInboxFilter?.insuranceCompany ?? undefined,
        filterQuoteInvoiceStatus: this.getSavedInvoiceStatus(),
        filterQuoteEocStatus: this.props.savedFixedInboxFilter?.eocStatus ?? '',
        filterQuoteTypes: (this.props.savedFixedInboxFilter?.quoteTypes ?? []).map(
          String
        ),
      },
      () => {
        if (this.props.savedInbox != undefined) {
          this.getInboxData()
        }
      }
    )
  }

  private openFavoriteModal = () => {
    this.setState({
      favoriteModalOpen: true,
      favoriteInboxes: this.state.inboxes
        .filter((inbox) => inbox.inboxType != 'line' && inbox.id != -1)
        .sort((a, b) =>
          a.inboxType == 'DynamicPersonal' && b.inboxType != 'DynamicPersonal'
            ? -1
            : a.inboxType != 'DynamicPersonal' && b.inboxType == 'DynamicPersonal'
              ? 1
              : 0
        )
        .map((inbox) =>
          inbox.inboxType == 'DynamicPersonal'
            ? { ...inbox, isUserFavorite: true }
            : inbox
        ),
    })
  }

  private closeFavoriteModal = () => {
    if (!this.state.favoriteSaving) {
      this.setState({ favoriteModalOpen: false })
    }
  }

  private toggleInboxFavorite = (inboxId: number) => {
    this.setState({
      favoriteInboxes: this.state.favoriteInboxes.map((inbox) =>
        inbox.id == inboxId && inbox.inboxType != 'DynamicPersonal'
          ? { ...inbox, isUserFavorite: !inbox.isUserFavorite }
          : inbox
      ),
    })
  }

  private saveInboxFavorites = async () => {
    const favoriteUpdates = this.state.favoriteInboxes
      .filter((inbox) => inbox.inboxType != 'DynamicPersonal')
      .map((inbox) => {
        const favorite = new WorkflowInboxUserFavorite()
        favorite.workflowInboxId = inbox.id
        favorite.markAsFavorite = inbox.isUserFavorite
        return favorite
      })

    await new Promise<void>((resolve) => {
      this.setState({ favoriteSaving: true, gridLoading: true }, resolve)
    })

    try {
      await updateInboxFavorites(favoriteUpdates)
      this.setState({ favoriteModalOpen: false })
      await this.loadInboxes()
      this.setState({ favoriteSaving: false, gridLoading: false })
    } catch {
      this.setState({ favoriteSaving: false, gridLoading: false })
    }
  }

  async componentDidMount(): Promise<void> {
    this.sort = this.sort.bind(this)
    this.closeModal = this.closeModal.bind(this)
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this)
    await this.loadInboxTypes()
  }

  private displayPriority: {
    [key: number]: { label: string; icon: typeof Thermometer; color: string }
  } = {
    1: { label: 'Normal', icon: ThermometerLow, color: '#757575' },
    2: { label: 'Medium', icon: Thermometer, color: '#f9a825' },
    3: { label: 'High', icon: ThermometerHigh, color: '#d32f2f' },
  }

  private priorityByName: { [key: string]: number } = {
    high: 3,
    medium: 2,
    normal: 1,
    low: 1,
  }

  private renderPriority = (priority: number | string | undefined) => {
    const value =
      typeof priority === 'string'
        ? (this.priorityByName[priority.trim().toLowerCase()] ?? Number(priority))
        : priority
    const display = value !== undefined ? this.displayPriority[value] : undefined
    if (!display) {
      return <TableCell align="center">{priority}</TableCell>
    }
    const PriorityIcon = display.icon
    return (
      <TableCell align="center">
        <Tooltip title={`${display.label} Priority`}>
          <PriorityIcon
            htmlColor={display.color}
            sx={{
              verticalAlign: 'middle',
              transition: 'transform 0.2s ease-out',
              '&:hover': { transform: 'translateY(-4px) scale(1.15)' },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          />
        </Tooltip>
      </TableCell>
    )
  }

  private renderOpenButton = (onClick: () => void) => (
    <Button
      variant="contained"
      color="primary"
      size="small"
      sx={compactButtonSx}
      onClick={onClick}
    >
      Open
    </Button>
  )

  private renderInboxTypeOptions = () => [
    <option key="empty-inbox-type" value=""></option>,
    ...this.availableInboxTypes().map((inboxType) => {
      const itemType = inboxType.id.toString() as InboxItemType

      return (
        <option key={itemType} value={itemType}>
          {inboxType.description || InboxTypeItemLabels[itemType]}
        </option>
      )
    }),
  ]

  private renderFavoriteInboxLinks = () => {
    if (!this.state.selectedType) {
      return null
    }

    const favoriteInboxes = this.state.inboxes.filter(
      (inbox) =>
        inbox.inboxType != 'line' &&
        (inbox.isUserFavorite || inbox.inboxType === 'DynamicPersonal')
    )

    if (favoriteInboxes.length === 0) {
      return null
    }

    return (
      <Grid item xs={12} style={{ paddingBottom: '10px' }}>
        <Typography variant="caption" display="block">
          CURRENT FAVORITES
        </Typography>
        <Grid container spacing={1} alignItems="center">
          {favoriteInboxes.map((inbox) => (
            <Grid item key={`favorite-inbox-${inbox.id}`}>
              <Chip
                clickable
                size="small"
                label={`${inbox.inboxName} (${inbox.itemCount ?? 0} items)`}
                onClick={() => this.setSelectedInbox({ target: { value: inbox.id } })}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
    )
  }

  private setSelectedInbox = async (event: any) => {
    const request = new SearchRequestBase()
    request.page = 1
    request.pageSize = this.state.pageSize

    const inboxId = event.target.value
    const inbox = this.state.inboxes.find((x) => x.id == inboxId)

    this.props.setSelectedInbox!(inbox)
    this.props.setSavedSort!(undefined, undefined)

    if (inbox && inbox.itemType == InboxItemType.Claim) {
      this.clearClaimFilter(false)
    }

    if (
      (inbox && inbox.itemType == InboxItemType.Quote) ||
      (inbox && inbox.itemType == InboxItemType.Invoice)
    ) {
      this.clearQuoteFilter(false)
    }

    this.setState(
      {
        selectedItemType: inbox?.itemType,
        inbox: inbox,
        gridLoading: inboxId != '',
        page: 1,
        orderBy: '',
        order: 'asc',
        dynamicColumns: new Array<string>(),
        gridData: new pagedList().items as
          | DocumentInboxItemSummary[]
          | IncomingDocumentSummary[]
          | ClaimInboxItemSummary[]
          | QuoteInboxItemSummary[]
          | BilloutInboxItemSummary[]
          | CaseInboxItemSummary[]
          | { [key: string]: any }[],
        pageCount: 0,
        firstRecordIndex: 0,
        lastRecordIndex: 0,
        totalItems: 0,
        bulkActions: new Array<InboxActionSummary>(),
        checkInboxItems: new Array<BulkActionItem>(),
      },
      async () => {
        if (!this.state.inbox) {
          return
        }
        if (this.state.inbox.id == -1) {
          await getAllIncomingDocuments(request).then((response: any) => {
            this.setState(
              {
                gridData: response.items,
                pageCount: response.totalPages,
                totalItems: response.totalItems,
                gridLoading: false,
                checkInboxItems: new Array<BulkActionItem>(),
              },
              () => {
                this.setPaginationOffsetData()
              }
            )
          })
        } else {
          getAllBulkActions(this.state.inbox.id.toString()).then((response: any) => {
            this.setState({
              bulkActions: response,
            })
          })

          await getAllInboxItems(request, this.state.inbox.id.toString(), 'default').then(
            (response: any) => {
              this.setState(
                {
                  dynamicColumns: response.columnNames,
                  gridData: response.items,
                  pageCount: response.totalPages,
                  totalItems: response.totalItems,
                  gridLoading: false,
                  checkInboxItems: new Array<BulkActionItem>(),
                },
                () => {
                  this.setPaginationOffsetData()
                }
              )
            }
          )
        }
      }
    )
  }

  private toNumber = (value: any) =>
    value === undefined || value === null || value === '' ? undefined : Number(value)

  private getDynamicBalanceDue = (record: any) => {
    const invoicedAmount = this.toNumber(record['hdn_invoicedamount'])
    if (invoicedAmount === undefined) {
      return undefined
    }
    const payment = this.toNumber(record['hdn_invoicepayment']) ?? 0
    return Math.round((invoicedAmount - payment) * 100) / 100
  }

  private hasUnbundledClaims = (record: any) =>
    Number(
      ('hdn_claimcount' in record ? record['hdn_claimcount'] : record['claim count']) ?? 0
    ) > 0

  private isTrue = (value: any) =>
    value === true || String(value).toLowerCase() === 'true'

  private renderAssignedUser = (assignedUser?: string) => {
    if (!assignedUser || assignedUser.length <= 12) {
      return <TableCell>{assignedUser}</TableCell>
    }
    return (
      <TableCell>
        <Tooltip title={assignedUser}>
          <span>{assignedUser.substring(0, 12)}...</span>
        </Tooltip>
      </TableCell>
    )
  }

  private renderPendCell = (pendDate?: Date | string, pendNote?: string) => (
    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
      {pendDate && moment(pendDate).format('L')}
      {pendNote && (
        <>
          &nbsp;
          <Tooltip title={pendNote}>
            <span style={{ verticalAlign: 'middle' }}>
              <CommentOutline fontSize="small" />
            </span>
          </Tooltip>
        </>
      )}
    </TableCell>
  )

  private renderLockedBy = (item: {
    lockedBy?: string
    lockedOn?: Date
    lockExpiresAt?: Date
    isLockExpired?: boolean
  }) => {
    const expiresAt = moment(item.lockExpiresAt).format('MM/DD/YYYY HH:mm:ss')

    if (item.isLockExpired && item.lockedBy != undefined) {
      return (
        <TableCell align="center">
          <Tooltip
            title={`Lock expired: was locked by ${item.lockedBy} until ${expiresAt}`}
          >
            <Alarm fontSize="small" htmlColor="#d32f2f" />
          </Tooltip>
        </TableCell>
      )
    }

    if (item.lockedBy != undefined) {
      return (
        <TableCell align="center">
          <Tooltip title={`Locked by ${item.lockedBy} until ${expiresAt}`}>
            <Alarm fontSize="small" />
          </Tooltip>
        </TableCell>
      )
    }

    return <TableCell />
  }

  private isGridDataEmpty = () => {
    return !this.state.gridData || this.state.gridData.length === 0
  }

  private paginationInfo = () => {
    if (!this.isGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  private handleCheckboxChange(event: React.ChangeEvent, value: boolean) {
    let items = this.state.checkInboxItems as Array<BulkActionItem>
    let result: string[] = event.target.id.split('|||')
    let inboxItem = new BulkActionItem()

    inboxItem.inboxItemId = +result[0]
    inboxItem.externalItemIdentifier = result[1]

    if (value) {
      items.push(inboxItem)
    } else {
      const item = items.filter((x) => x.inboxItemId == inboxItem.inboxItemId)
      const index = item.map((filteredNum) => item.indexOf(filteredNum))[0]

      if (index > -1) {
        items.splice(index, 1)
      }
    }

    this.setState({ checkInboxItems: items })
  }

  private mapClaimData = () => {
    const claimItems = this.state.gridData as ClaimInboxItemSummary[]

    return claimItems.map((claim) => (
      <TableRow key={claim.id}>
        {this.state.bulkActions.length > 0 && (
          <TableCell className="checkboxCell">
            <Checkbox
              color="primary"
              id={`${claim.id}|||${claim.eocLegacy}`}
              onChange={this.handleCheckboxChange}
              checked={this.state.checkInboxItems.some(
                (item) => item.inboxItemId === claim.id
              )}
            />
          </TableCell>
        )}
        <TableCell className="openButtonCell">
          {this.renderOpenButton(() => this.openClaimItem(claim.associatedItemId))}
        </TableCell>
        {this.renderPriority(claim.priority)}
        <TableCell align="right">{moment(claim.entryDate).format('L')}</TableCell>
        <TableCell style={{ whiteSpace: 'nowrap' }}>
          {claim.eocLegacy}&nbsp;&nbsp;
          {claim.appealType != undefined ? (
            <Tooltip title={AppealNameConstants[claim.appealType]}>
              <InfoOutlined fontSize="small" />
            </Tooltip>
          ) : (
            <></>
          )}
          {claim.id && claim.lastAppealDate && claim.appealType == undefined ? (
            <Tooltip title={'Show Appeal Details'}>
              <ReceiptOutlined
                style={{ cursor: 'pointer' }}
                fontSize="small"
                onClick={() => {
                  this.props.openAppealLogModal!(claim.associatedItemId)
                  this.setState({ groupInfo: claim.eocLegacy })
                }}
              />
            </Tooltip>
          ) : (
            <></>
          )}
        </TableCell>
        <TableCell align="right">{moment(claim.dateOfService).format('L')}</TableCell>
        <TableCell>{claim.patientName}</TableCell>
        <TableCell>{claim.claimType}</TableCell>
        <TableCell>
          {claim.inGlobal ? <Check htmlColor="green" /> : <Close htmlColor="#9e9e9e" />}
        </TableCell>
        <TableCell>{claim.scheduleComponent}</TableCell>
        <TableCell>
          {claim.billingName}&nbsp;&nbsp;
          <Tooltip title={claim.providerLocation || ''}>
            <InfoOutlined fontSize="small" />
          </Tooltip>
        </TableCell>
        <TableCell align="right">
          {claim.totalCharges ? formatCurrency(claim.totalCharges?.toString()) : ''}
        </TableCell>
        <TableCell align="right">
          {claim.totalContract ? formatCurrency(claim.totalContract.toString()) : ''}
        </TableCell>
        {this.renderAssignedUser(claim.assignedUser)}
        {this.renderPendCell(claim.pendDate, claim.pendNote)}
        {this.renderLockedBy(claim)}
      </TableRow>
    ))
  }

  private mapDocumentData = () => {
    const docItems = this.state.gridData as DocumentInboxItemSummary[]

    return docItems.map((doc) => (
      <TableRow key={doc.id}>
        {this.state.bulkActions.length > 0 && (
          <TableCell className="checkboxCell">
            <Checkbox
              color="primary"
              id={`${doc.id}|||${doc.fileName}`}
              onChange={this.handleCheckboxChange}
              checked={this.state.checkInboxItems.some(
                (item) => item.inboxItemId === doc.id
              )}
            />
          </TableCell>
        )}
        <TableCell className="openButtonCell">
          {this.renderOpenButton(() => this.openDocumentItem(doc.associatedItemId))}
        </TableCell>
        {this.renderPriority(doc.priority)}
        <TableCell align="right">{moment(doc.entryDate).format('L')}</TableCell>
        <TableCell style={{ minWidth: '200px' }}>{doc.fileName}</TableCell>
        {this.renderAssignedUser(doc.assignedUser)}
        {this.renderPendCell(doc.pendDate, doc.pendNote)}
        {this.renderLockedBy(doc)}
      </TableRow>
    ))
  }

  private mapQuoteData = () => {
    const quoteItems = this.state.gridData as QuoteInboxItemSummary[]

    return quoteItems.map((quote) => (
      <TableRow key={quote.id}>
        {this.state.bulkActions.length > 0 && (
          <TableCell className="checkboxCell">
            <Checkbox
              color="primary"
              id={`${quote.id}|||${quote.eocLegacy}-${quote.groupNumber}`}
              onChange={this.handleCheckboxChange}
              checked={this.state.checkInboxItems.some(
                (item) => item.inboxItemId === quote.id
              )}
            />
          </TableCell>
        )}
        <TableCell className="openButtonCell">
          {this.renderOpenButton(() => this.openQuoteItem(quote.associatedItemId))}
        </TableCell>
        <TableCell>
          <Tooltip
            title={
              quote.eocUnbundledClaims
                ? 'Bundle Claims - There are claims that are not in any bundle'
                : 'Bundle Claims'
            }
          >
            <AddShoppingCart
              sx={{
                cursor: 'pointer',
                color: quote.eocUnbundledClaims ? 'red' : 'text.primary',
              }}
              onClick={() => {
                this.setState({ gridLoading: true }, () => {
                  this.props.openBillsDialog!(quote.associatedItemId).then(() =>
                    this.setState({ gridLoading: false })
                  )
                })
              }}
            />
          </Tooltip>
        </TableCell>
        <TableCell className="openButtonCell">
          <InvoiceStatusIcon
            isInvoiced={quote.invoicedAmount != undefined}
            quoteStatus={quote.quoteStatus}
            invoicedAmount={quote.invoicedAmount}
            balanceDue={quote.balanceDue}
            initialInvoicePrintDate={quote.initialInvoicePrintDate}
          />
        </TableCell>
        {this.renderPriority(quote.priority)}
        <TableCell align="right">{moment(quote.entryDate).format('L')}</TableCell>
        <TableCell align="left">
          {quote.eocLegacy}
          {quote.eocStatus && (
            <div style={{ fontSize: '12px', color: '#757575' }}>{quote.eocStatus}</div>
          )}
        </TableCell>
        <TableCell align="left" style={{ paddingLeft: '0px' }}>
          {quote.groupNumber}
        </TableCell>
        <TableCell>{quote.quoteType}</TableCell>
        <TableCell align="right">
          {quote.startDate ? moment(quote.startDate).format('MM/DD/YYYY') : ''}
        </TableCell>
        <TableCell style={{ padding: '0px' }}></TableCell>
        <TableCell align="right">
          {quote.endDate ? moment(quote.endDate).format('MM/DD/YYYY') : ''}
        </TableCell>
        <TableCell>{quote.quoteStatus}</TableCell>
        <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
          {(quote.claimCount ?? 0) > 0 && (
            <Tooltip
              title={`Based on ${quote.claimCount} bundled non-PT claim${
                quote.claimCount! > 1 ? 's' : ''
              }`}
            >
              <span>{(quote.overallPercentage ?? 0).toString() + '%'}</span>
            </Tooltip>
          )}
        </TableCell>
        <TableCell>{quote.patientName}</TableCell>
        <TableCell>{quote.insuranceCompanyName}</TableCell>
        <TableCell align="right">
          {quote.quoteTotal ? formatCurrency(quote.quoteTotal.toString()) : ''}
        </TableCell>
        <TableCell align="right">
          {quote.reconciledTotal != undefined && quote.reconciledTotal > 0
            ? formatCurrency(quote.reconciledTotal.toString())
            : ''}
        </TableCell>
        {this.renderAssignedUser(quote.assignedUser)}
        {this.renderPendCell(quote.pendDate, quote.pendNote)}
        {this.renderLockedBy(quote)}
      </TableRow>
    ))
  }

  private mapBilloutData = () => {
    const billoutItems = this.state.gridData as BilloutInboxItemSummary[]

    return billoutItems.map((billout) => (
      <TableRow key={billout.id}>
        {this.state.bulkActions.length > 0 && (
          <TableCell className="checkboxCell">
            <Checkbox
              color="primary"
              id={`${billout.id}|||${billout.eocLegacy}`}
              onChange={this.handleCheckboxChange}
              checked={this.state.checkInboxItems.some(
                (item) => item.inboxItemId === billout.id
              )}
            />
          </TableCell>
        )}
        <TableCell className="openButtonCell">
          {this.renderOpenButton(() => this.openBilloutItem(billout.associatedItemId))}
        </TableCell>
        {this.renderPriority(billout.priority)}
        <TableCell align="right">{moment(billout.entryDate).format('L')}</TableCell>
        <TableCell align="right">{billout.daysInInbox}</TableCell>
        <TableCell>{billout.eocLegacy}</TableCell>
        <TableCell>{billout.patientName}</TableCell>
        <TableCell>
          {billout.dateOfService
            ? moment(billout.dateOfService).format('MM/DD/YYYY')
            : ''}
        </TableCell>
        <TableCell>{billout.providerGroup}</TableCell>
        <TableCell>{billout.eocStatus}</TableCell>
        <TableCell>{billout.bundleType}</TableCell>
        <TableCell>{billout.insuranceCompanyName}</TableCell>
        <TableCell align="right">
          {billout.amountBilled != undefined
            ? formatCurrency(billout.amountBilled.toString())
            : ''}
        </TableCell>
        <TableCell align="right">
          {billout.balanceDue != undefined
            ? formatCurrency(billout.balanceDue.toString())
            : ''}
        </TableCell>
        {this.renderAssignedUser(billout.assignedUser)}
        {this.renderPendCell(billout.pendDate, billout.pendNote)}
        {this.renderLockedBy(billout)}
      </TableRow>
    ))
  }

  private mapCaseData = () => {
    const caseItems = this.state.gridData as CaseInboxItemSummary[]

    return caseItems.map((medCase) => (
      <TableRow key={medCase.id}>
        {this.state.bulkActions.length > 0 && (
          <TableCell className="checkboxCell">
            <Checkbox
              color="primary"
              id={`${medCase.id}|||${medCase.caseNumber}`}
              onChange={this.handleCheckboxChange}
              checked={this.state.checkInboxItems.some(
                (item) => item.inboxItemId === medCase.id
              )}
            />
          </TableCell>
        )}
        <TableCell className="openButtonCell">
          {this.renderOpenButton(() => this.openCaseItem(medCase.associatedItemId))}
        </TableCell>
        <TableCell>
          {!medCase.initialCasePaidInFull && medCase.hasInitialCaseDetails && (
            <Tooltip
              style={{ cursor: 'pointer', color: 'red' }}
              title="Initial Case Line Item has not been paid in full"
            >
              <MoneyOff />
            </Tooltip>
          )}
        </TableCell>
        {this.renderPriority(medCase.priority)}
        <TableCell align="right">{moment(medCase.entryDate).format('L')}</TableCell>
        <TableCell>{medCase.caseNumber}</TableCell>
        <TableCell>{medCase.patientName}</TableCell>
        <TableCell align="right">{medCase.referralDateDisplay}</TableCell>
        <TableCell align="right">{medCase.dueDateDisplay}</TableCell>
        <TableCell align="right">{medCase.dateRecordsReceivedDisplay}</TableCell>
        <TableCell>{medCase.typeDescription}</TableCell>
        <TableCell>{medCase.referralSource}</TableCell>
        <TableCell align="right">{medCase.dateOfServiceDisplay}</TableCell>
        <TableCell>{medCase.providerName}</TableCell>
        {this.renderAssignedUser(medCase.assignedUser)}
        {this.renderPendCell(medCase.pendDate, medCase.pendNote)}
        {this.renderLockedBy(medCase)}
      </TableRow>
    ))
  }

  private mapInvoiceData = () => {
    const billoutItems = this.state.gridData as InvoiceInboxItemSummary[]

    return billoutItems.map((invoice) => (
      <TableRow key={invoice.id}>
        {this.state.bulkActions.length > 0 && (
          <TableCell className="checkboxCell">
            <Checkbox
              color="primary"
              id={`${invoice.id}|||${invoice.invoiceNumberAndSuffix}`}
              onChange={this.handleCheckboxChange}
              checked={this.state.checkInboxItems.some(
                (item) => item.inboxItemId === invoice.id
              )}
            />
          </TableCell>
        )}
        <TableCell className="openButtonCell">
          {this.renderOpenButton(() => this.openInvoiceItem(invoice.associatedItemId))}
        </TableCell>
        {this.renderPriority(invoice.priority)}
        <TableCell align="right">{moment(invoice.entryDate).format('L')}</TableCell>
        <TableCell align="right">{invoice.daysSinceInvoiced}</TableCell>
        <TableCell>{invoice.invoiceNumberAndSuffix}</TableCell>
        <TableCell>{invoice.quoteType}</TableCell>
        <TableCell align="right">{moment(invoice.quoteStartDate).format('L')}</TableCell>
        <TableCell align="right">{invoice.quoteGroupNumber}</TableCell>
        <TableCell>{invoice.patientName}</TableCell>
        <TableCell>{invoice.insuranceCompany}</TableCell>
        <TableCell>{invoice.adjusterName}</TableCell>
        <TableCell align="right">
          {invoice.invoiceAmount != undefined
            ? formatCurrency(invoice.invoiceAmount.toString())
            : ''}
        </TableCell>
        <TableCell align="right">
          {invoice.totalPaid != undefined
            ? formatCurrency(invoice.totalPaid.toString())
            : ''}
        </TableCell>
        <TableCell align="right">
          {invoice.balanceDue != undefined
            ? formatCurrency(invoice.balanceDue.toString())
            : ''}
        </TableCell>
        {this.renderAssignedUser(invoice.assignedUser)}
        {this.renderPendCell(invoice.pendDate, invoice.pendNote)}
        {this.renderLockedBy(invoice)}
      </TableRow>
    ))
  }

  private mapGridData = () => {
    if (this.isGridDataEmpty()) {
      let columns = this.state.dynamicColumns?.length

      if (!this.state.inbox.inboxType.includes('Dynamic')) {
        switch (this.state.selectedItemType.toString()) {
          case InboxItemType.Claim:
            columns = 15
            break
          case InboxItemType.Document:
            columns = 8
            break
          case InboxItemType.Quote:
            columns = 17
            break
          case InboxItemType.Billout:
            columns = 14
            break
          case InboxItemType.Case:
            columns = 15
            break
          case InboxItemType.Invoice:
            columns = 15
            break
          default:
            columns = 9
            break
        }

        if (this.state.bulkActions.length > 0) {
          columns++
        }
      }
      return (
        <TableRow key={12}>
          <TableCell colSpan={columns + 6} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      if (!this.state.inbox.inboxType.includes('Dynamic')) {
        switch (this.state.selectedItemType.toString()) {
          case InboxItemType.Claim:
            return this.mapClaimData()
          case InboxItemType.Document:
            return this.mapDocumentData()
          case InboxItemType.Quote:
            return this.mapQuoteData()
          case InboxItemType.Billout:
            return this.mapBilloutData()
          case InboxItemType.Invoice:
            return this.mapInvoiceData()
          case InboxItemType.Case:
            return this.mapCaseData()
          default:
            if (this.state.inbox?.id == -1) {
              const docs = this.state.gridData as IncomingDocumentSummary[]

              return docs.map((doc) => (
                <TableRow key={doc.name}>
                  <TableCell className="openButtonCell">
                    {this.renderOpenButton(() => this.openNewlyAddedDocument(doc))}
                  </TableCell>
                  <TableCell align="right">
                    {moment(doc.incomingDate).format('L')}
                  </TableCell>
                  <TableCell style={{ minWidth: '550px' }}>{doc.name}</TableCell>
                </TableRow>
              ))
            }
            break
        }
      } else {
        return this.state.gridData.map((dynamicRecord, index) => (
          <TableRow key={index}>
            {this.state.dynamicColumns?.map((column) =>
              this.renderDynamicColumn(column, dynamicRecord)
            )}
          </TableRow>
        ))
      }

      return <></>
    }
  }

  public sort(columnName: string) {
    this.setState(
      {
        orderBy: columnName,
        order: this.state.order == 'asc' ? 'desc' : 'asc',
        gridLoading: true,
        page: 1,
      },
      () => {
        this.props.setSavedSort!(this.state.order, this.state.orderBy)
        this.getInboxData(false, false, true).catch(() => {})
      }
    )
  }

  private renderSortedHeaderIfNotNewlyAdded = (
    columnName: string,
    propertyName: string,
    align?: any,
    style?: React.CSSProperties
  ) => {
    return this.state.inbox?.id != -1 ? (
      <SortedTableCell
        columnName={columnName}
        propertyName={propertyName}
        align={align}
        style={style}
        order={this.state.order}
        orderBy={this.state.orderBy}
        onClick={this.sort}
        reserveSortIconSpace
      />
    ) : (
      <></>
    )
  }

  private isPendNoteColumn = (columnName: string) =>
    columnName.toLowerCase().replace(/[\s_]/g, '') === 'pendnote'

  private renderDynamicHeaderColumn = (columnName: string, columnIndex: string) => {
    if (columnName.toLowerCase().replace(/[\s_]/g, '') === 'penddate') {
      return this.renderSortedHeaderIfNotNewlyAdded('Pend', columnIndex, 'right')
    }
    if (this.isPendNoteColumn(columnName)) {
      return null
    }
    if (columnName == 'hdn_AssociatedItemId') {
      if (this.state.inbox.itemType == InboxItemType.Quote) {
        return (
          <>
            <TableCell className="openButtonCell"></TableCell>
            <TableCell></TableCell>
            <TableCell className="openButtonCell"></TableCell>
          </>
        )
      } else {
        return <TableCell className="openButtonCell"></TableCell>
      }
    } else if (columnName.includes('hdn_')) {
      return <TableCell style={{ display: 'none' }}></TableCell>
    } else {
      let align = this.dynamicColumnAlignment(columnName)
      let style =
        this.state.selectedItemType == InboxItemType.Quote &&
        (columnName === 'EOC/Legacy' || columnName === 'Start Date')
          ? { paddingRight: '0px' }
          : undefined

      if (style) {
        if (
          this.state.selectedItemType == InboxItemType.Quote &&
          columnName === 'Start Date'
        ) {
          return (
            <>
              {this.renderSortedHeaderIfNotNewlyAdded(
                columnName,
                columnIndex,
                align,
                style
              )}
              <TableCell style={{ padding: '0px', width: '20px' }} align="left">
                &nbsp;&nbsp;
                <Tooltip
                  title={`Represents Surgery Date when Surgical or Injection bundle`}
                >
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </TableCell>
            </>
          )
        }

        return this.renderSortedHeaderIfNotNewlyAdded(
          columnName,
          columnIndex,
          align,
          style
        )
      }

      return this.renderSortedHeaderIfNotNewlyAdded(columnName, columnIndex, align)
    }
  }

  private renderDynamicColumn = (columnName: string, record: any) => {
    if (columnName == 'hdn_AssociatedItemId') {
      if (this.state.selectedItemType == InboxItemType.Claim) {
        return (
          <TableCell className="openButtonCell">
            {this.renderOpenButton(() =>
              this.openClaimItem(record[columnName.toLowerCase()])
            )}
          </TableCell>
        )
      } else if (this.state.selectedItemType == InboxItemType.Document) {
        return (
          <TableCell className="openButtonCell">
            {this.renderOpenButton(() =>
              this.openDocumentItem(record[columnName.toLowerCase()])
            )}
          </TableCell>
        )
      } else if (this.state.selectedItemType == InboxItemType.Quote) {
        return (
          <>
            <TableCell className="openButtonCell">
              {this.renderOpenButton(() =>
                this.openQuoteItem(record[columnName.toLowerCase()])
              )}
            </TableCell>
            <TableCell>
              <Tooltip
                title={
                  this.hasUnbundledClaims(record)
                    ? 'Bundle Claims - There are claims that are not in any bundle'
                    : 'Bundle Claims'
                }
              >
                <AddShoppingCart
                  sx={{
                    cursor: 'pointer',
                    color: this.hasUnbundledClaims(record) ? 'red' : 'text.primary',
                  }}
                  onClick={() => {
                    this.setState({ gridLoading: true }, () => {
                      this.props.openBillsDialog!(record[columnName.toLowerCase()]).then(
                        () => this.setState({ gridLoading: false })
                      )
                    })
                  }}
                />
              </Tooltip>
            </TableCell>
            <TableCell className="openButtonCell">
              <InvoiceStatusIcon
                isInvoiced={this.isTrue(record['hdn_invoiced'])}
                initialInvoicePrintDate={record['hdn_initalinvoiceprintdate']}
                invoicedAmount={this.toNumber(record['hdn_invoicedamount'])}
                balanceDue={this.getDynamicBalanceDue(record)}
              />
            </TableCell>
          </>
        )
      } else if (this.state.selectedItemType == InboxItemType.Billout) {
        return (
          <TableCell className="openButtonCell">
            {this.renderOpenButton(() =>
              this.openBilloutItem(record[columnName.toLowerCase()])
            )}
          </TableCell>
        )
      } else if (this.state.selectedItemType == InboxItemType.Case) {
        return (
          <TableCell className="openButtonCell">
            {this.renderOpenButton(() =>
              this.openCaseItem(record[columnName.toLowerCase()])
            )}
          </TableCell>
        )
      } else if (this.state.selectedItemType == InboxItemType.Invoice) {
        return (
          <TableCell className="openButtonCell">
            {this.renderOpenButton(() =>
              this.openInvoiceItem(record[columnName.toLowerCase()])
            )}
          </TableCell>
        )
      }
    }

    if (columnName.indexOf('hdn_') > -1) {
      return (
        <TableCell style={{ display: 'none' }}>
          {record[columnName.toLowerCase()]}
        </TableCell>
      )
    } else if (columnName.toLowerCase() === 'priority') {
      return this.renderPriority(this.toNumber(record[columnName.toLowerCase()]))
    } else if (
      ['assignedto', 'assigneduser', 'assignto'].includes(
        columnName.toLowerCase().replace(/[\s_]/g, '')
      )
    ) {
      return this.renderAssignedUser(record[columnName.toLowerCase()])
    } else if (this.isPendNoteColumn(columnName)) {
      return null
    } else if (
      this.state.selectedItemType == InboxItemType.Quote &&
      columnName.toLowerCase().replace(/[\s_]/g, '') === 'eoc/legacy'
    ) {
      const eocStatus = record['hdn_eocstatus']
      return (
        <TableCell>
          {record[columnName.toLowerCase()]}
          {eocStatus && (
            <div style={{ fontSize: '12px', color: '#757575' }}>{eocStatus}</div>
          )}
        </TableCell>
      )
    } else if (columnName.toLowerCase().replace(/[\s_]/g, '') === 'penddate') {
      const pendNoteColumn = this.state.dynamicColumns?.find(this.isPendNoteColumn)
      const pendDate = record[columnName.toLowerCase()]
      return this.renderPendCell(
        pendDate ? toISODate(pendDate) : undefined,
        pendNoteColumn ? record[pendNoteColumn.toLowerCase()] : undefined
      )
    } else {
      let align = this.dynamicColumnAlignment(columnName)
      let formattedValue = this.dynamicColumnFormatted(
        columnName,
        record[columnName.toLowerCase()]
      )

      if (
        formattedValue.toLowerCase() == 'true' ||
        formattedValue.toLowerCase() == 'false'
      ) {
        return (
          <TableCell>
            {formattedValue.toLowerCase() == 'true' ? (
              <Check htmlColor="green" />
            ) : (
              <Close htmlColor="#9e9e9e" />
            )}
          </TableCell>
        )
      }

      if (
        this.state.selectedItemType == InboxItemType.Quote &&
        columnName === 'Start Date'
      ) {
        return (
          <>
            <TableCell style={{ paddingRight: '0px' }} align={align as any}>
              {formattedValue}
            </TableCell>
            <TableCell style={{ padding: '0px' }}></TableCell>
          </>
        )
      }

      return <TableCell align={align as any}>{formattedValue}</TableCell>
    }
  }

  private dynamicColumnAlignment = (columnName: string) => {
    if (columnName.toLowerCase() === 'priority') {
      return 'center'
    }
    return columnName.toLowerCase().includes('date') ||
      columnName.toLowerCase().includes('total') ||
      columnName.toLowerCase().includes('amt') ||
      columnName.toLowerCase().includes('amount') ||
      columnName.toLowerCase().includes('balance') ||
      columnName.toLowerCase().includes('owed') ||
      columnName.toLowerCase().includes('owes') ||
      columnName.toLowerCase().includes('count') ||
      columnName.toLowerCase().includes('%') ||
      columnName.toLowerCase().includes('rcv') ||
      columnName.toLowerCase().includes('dos') ||
      (columnName.toLowerCase().includes('#') && columnName !== '#')
      ? 'right'
      : 'left'
  }

  private dynamicColumnFormatted = (columnName: string, value: string) => {
    let formattedValue = value

    if (
      (columnName.toLowerCase().includes('date') ||
        columnName.toLowerCase().includes('rcv') ||
        columnName.toLowerCase().includes('dos')) &&
      value
    ) {
      formattedValue = moment(toISODate(value)).format('L')
    } else if (
      (columnName.toLowerCase().includes('total') ||
        columnName.toLowerCase().includes('amt') ||
        columnName.toLowerCase().includes('amount') ||
        columnName.toLowerCase().includes('balance') ||
        columnName.toLowerCase().includes('owed') ||
        columnName.toLowerCase().includes('owes')) &&
      value
    ) {
      formattedValue = formatCurrency(value)
    }

    return formattedValue
  }

  private mapClaimHeaders = () => {
    return (
      <>
        {this.renderSortedHeaderIfNotNewlyAdded('EOC/Legacy', 'EOCLegacy')}
        {this.renderSortedHeaderIfNotNewlyAdded(
          'Date of Service',
          'DateOfService',
          'right'
        )}
        {this.renderSortedHeaderIfNotNewlyAdded('Patient', 'PatientName')}
        {this.renderSortedHeaderIfNotNewlyAdded('Type', 'ClaimType')}
        {this.renderSortedHeaderIfNotNewlyAdded('In Global', 'InGlobal')}
        {this.renderSortedHeaderIfNotNewlyAdded('Component', 'ScheduleComponent')}
        {this.renderSortedHeaderIfNotNewlyAdded(
          'Billing Info',
          'BillingName',
          undefined,
          {
            minWidth: '250px',
          }
        )}
        {this.renderSortedHeaderIfNotNewlyAdded('Total Charges', 'TotalCharges', 'right')}
        {this.renderSortedHeaderIfNotNewlyAdded(
          'Total Contract',
          'TotalContract',
          'right'
        )}
      </>
    )
  }

  private mapDocumentHeaders = () => {
    return <>{this.renderSortedHeaderIfNotNewlyAdded('Document Name', 'FileName')}</>
  }

  private mapQuoteHeaders = () => {
    return (
      <>
        {this.renderSortedHeaderIfNotNewlyAdded('EOC/Legacy', 'EOCLegacy', 'left', {
          paddingRight: '0px',
        })}
        {this.renderSortedHeaderIfNotNewlyAdded('#', 'GroupNumber', 'left', {
          paddingLeft: '0px',
        })}
        {this.renderSortedHeaderIfNotNewlyAdded('Quote Type', 'QuoteType')}
        {this.renderSortedHeaderIfNotNewlyAdded('Start Date', 'StartDate', 'right')}
        <TableCell style={{ padding: '0px' }}>
          <Tooltip title={`Represents Surgery Date when Surgical or Injection bundle`}>
            <InfoOutlined fontSize="small" />
          </Tooltip>
        </TableCell>
        {this.renderSortedHeaderIfNotNewlyAdded('End Date', 'EndDate', 'right')}
        {this.renderSortedHeaderIfNotNewlyAdded('Quote Status', 'QuoteStatus')}
        {this.renderSortedHeaderIfNotNewlyAdded(
          'Claim Recon %',
          'OverallPercentage',
          'right'
        )}
        {this.renderSortedHeaderIfNotNewlyAdded('Patient', 'PatientName')}
        {this.renderSortedHeaderIfNotNewlyAdded(
          'Insurance Company',
          'InsuranceCompanyName'
        )}
        {this.renderSortedHeaderIfNotNewlyAdded('Quoted $', 'QuoteTotal', 'right')}
        {this.renderSortedHeaderIfNotNewlyAdded(
          'Reconciled $',
          'ReconciledTotal',
          'right'
        )}
      </>
    )
  }

  private mapBilloutHeaders = () => {
    return (
      <>
        {this.renderSortedHeaderIfNotNewlyAdded('Days in Inbox', 'DaysInInbox', 'right')}
        {this.renderSortedHeaderIfNotNewlyAdded('EOC/Legacy', 'EOCLegacy')}
        {this.renderSortedHeaderIfNotNewlyAdded('Patient', 'PatientName')}
        {this.renderSortedHeaderIfNotNewlyAdded('DOS', 'DateOfService', 'right')}
        {this.renderSortedHeaderIfNotNewlyAdded('Provider Group', 'ProviderGroup')}
        {this.renderSortedHeaderIfNotNewlyAdded('EOC Status', 'EOCStatus')}
        {this.renderSortedHeaderIfNotNewlyAdded('Bundle Type', 'BundleType')}
        {this.renderSortedHeaderIfNotNewlyAdded('Ins Company', 'InsuranceCompanyName')}
        {this.renderSortedHeaderIfNotNewlyAdded('Amt Billed', 'AmountBilled', 'right')}
        {this.renderSortedHeaderIfNotNewlyAdded('Balance Due', 'BalanceDue', 'right')}
      </>
    )
  }

  private mapCaseHeaders = () => {
    return (
      <>
        {this.renderSortedHeaderIfNotNewlyAdded('Case Number', 'CaseNumber')}
        {this.renderSortedHeaderIfNotNewlyAdded('Patient', 'PatientName')}
        {this.renderSortedHeaderIfNotNewlyAdded(
          'Referral Date',
          'ReferralDateDisplay',
          'right'
        )}
        {this.renderSortedHeaderIfNotNewlyAdded(
          'Date For Rush',
          'DueDateDisplay',
          'right'
        )}
        {this.renderSortedHeaderIfNotNewlyAdded(
          'Records Received',
          'DateRecordsReceivedDisplay',
          'right'
        )}
        {this.renderSortedHeaderIfNotNewlyAdded('Type Description', 'TypeDescription')}
        {this.renderSortedHeaderIfNotNewlyAdded('Referral Source', 'ReferralSource')}
        {this.renderSortedHeaderIfNotNewlyAdded('DOS', 'dateOfServiceDisplay', 'right')}
        {this.renderSortedHeaderIfNotNewlyAdded('Provider', 'ProviderName')}
      </>
    )
  }

  private mapNewDocumentHeaders = () => {
    return (
      <>
        <TableCell>Document Name</TableCell>
      </>
    )
  }

  private mapInvoiceHeaders = () => {
    return (
      <>
        {this.renderSortedHeaderIfNotNewlyAdded(
          'Days Since Sent',
          'daysSinceInvoiced',
          'right'
        )}
        {this.renderSortedHeaderIfNotNewlyAdded('Invoice #', 'InvoiceNumberAndSuffix')}
        {this.renderSortedHeaderIfNotNewlyAdded('Quote Type', 'QuoteType')}
        {this.renderSortedHeaderIfNotNewlyAdded('Start Date', 'QuoteStartDate', 'right')}
        {this.renderSortedHeaderIfNotNewlyAdded('#', 'quoteGroupNumber', 'right')}
        {this.renderSortedHeaderIfNotNewlyAdded('Patient', 'PatientName')}
        {this.renderSortedHeaderIfNotNewlyAdded('Ins. Company', 'InsuranceCompany')}
        {this.renderSortedHeaderIfNotNewlyAdded('Adjuster', 'AdjusterName')}
        {this.renderSortedHeaderIfNotNewlyAdded('Invoice Amt.', 'InvoiceAmount', 'right')}
        {this.renderSortedHeaderIfNotNewlyAdded('Total Paid', 'TotalPaid', 'right')}
        {this.renderSortedHeaderIfNotNewlyAdded('Balance Due', 'BalanceDue', 'right')}
      </>
    )
  }

  private openNewlyAddedDocument = async (summary: IncomingDocumentSummary) => {
    this.props.rerouteToPath!(
      `/workflow/document/file/${encodeURIComponent(summary.name)}`
    )
  }

  private openDocumentItem = (associatedItemId: string) => {
    this.props.rerouteToPath!(
      `/workflow/document/${associatedItemId}/inbox/${this.state.inbox.id}`
    )
  }

  private openClaimItem = (associatedItemId: string) => {
    this.props.rerouteToPath!(
      `/workflow/claim/${associatedItemId}/inbox/${this.state.inbox.id}`
    )
  }

  private openQuoteItem = (associatedItemId: string) => {
    this.props.rerouteToPath!(
      `/workflow/quote/${associatedItemId}/inbox/${this.state.inbox.id}`
    )
  }

  private openBilloutItem = (associatedItemId: string) => {
    this.props.rerouteToPath!(
      `/workflow/billout/${associatedItemId}/inbox/${this.state.inbox.id}`
    )
  }

  private openInvoiceItem = (associatedItemId: string) => {
    this.props.rerouteToPath!(
      `/workflow/invoice/${associatedItemId}/inbox/${this.state.inbox.id}`
    )
  }

  private openCaseItem = (associatedItemId: string) => {
    this.props.rerouteToPath!(
      `/workflow/case/${associatedItemId}/inbox/${this.state.inbox.id}`
    )
  }

  private getInboxData = async (
    saveSearch: boolean = false,
    paging: boolean = false,
    sort: boolean = false
  ) => {
    this.setState({ gridLoading: true })

    const requestData = new FixedInboxSearchRequest()
    requestData.page = this.state.page
    requestData.pageSize = this.state.pageSize

    if (this.state.inbox?.id == -1) {
      await getAllIncomingDocuments(requestData).then((response: any) => {
        this.setState(
          {
            gridData: response.items,
            pageCount: response.totalPages,
            totalItems: response.totalItems,
            gridLoading: false,
          },
          () => {
            this.setPaginationOffsetData()
          }
        )
      })
    } else {
      await getAllBulkActions(this.state.inbox.id.toString()).then((response: any) => {
        this.setState({
          bulkActions: response,
        })
      })

      if (this.state.inbox.itemType == InboxItemType.Claim) {
        requestData.providerGroup = this.state.filterClaimProviderGroup
        requestData.providerTypeAbbreviation =
          this.state.filterClaimProviderTypeAbbreviation
        requestData.providerGroupPaymentTier = this.state.filterQuoteReconcileUrgency
      } else if (
        this.state.inbox.itemType == InboxItemType.Quote ||
        this.state.inbox.itemType == InboxItemType.Invoice
      ) {
        requestData.insuranceCompany = this.state.filterQuoteInsuranceCompany
        requestData.providerGroupPaymentTier = this.state.filterQuoteReconcileUrgency
        if (this.state.inbox.itemType == InboxItemType.Quote) {
          requestData.hasInvoice =
            this.state.filterQuoteInvoiceStatus === 'Invoiced' || undefined
          requestData.notInvoiced =
            this.state.filterQuoteInvoiceStatus === 'NotInvoiced' || undefined
          requestData.eocStatus = this.state.filterQuoteEocStatus || undefined
          requestData.quoteTypes = this.state.filterQuoteTypes.length
            ? this.state.filterQuoteTypes.map(Number)
            : undefined
        }
      }

      await getAllInboxItems(
        requestData,
        this.state.inbox.id.toString(),
        `${this.state.order}(${this.state.orderBy})`
      ).then((response: any) => {
        const newState: any = {
          dynamicColumns: response.columnNames,
          gridData: response.items,
          pageCount: response.totalPages,
          totalItems: response.totalItems,
          gridLoading: false,
        }

        if (!paging && !sort) {
          newState.checkInboxItems = new Array<BulkActionItem>()
        }

        this.setState(newState, () => {
          this.setPaginationOffsetData()

          if (saveSearch) {
            this.props.setFixedImportRequest!(requestData)
          }
        })
      })
    }
  }

  private setPaginationOffsetData = () => {
    const { page, totalItems, pageSize } = this.state
    const firstRecordIndex = totalItems > 0 ? (page - 1) * pageSize + 1 : 0
    const lastRecordIndex = page * pageSize < totalItems ? page * pageSize : totalItems
    this.setState({
      firstRecordIndex: firstRecordIndex,
      lastRecordIndex: lastRecordIndex,
    })
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1 }, () => this.getInboxData(false, true))
  }

  private setSelectedType = (event: SelectChangeEvent<InboxItemType>) => {
    const selectedType = event.target.value as InboxItemType

    if (!selectedType) {
      this.props.setSelectedInboxType!(undefined)
      this.props.setSelectedInbox!(undefined)
      this.setState({
        selectedType: undefined,
        selectedItemType: '',
        inbox: undefined as unknown as InboxSummary,
        inboxes: new Array<InboxSummary>(),
        dynamicColumns: new Array<string>(),
        gridData: new pagedList().items as
          | DocumentInboxItemSummary[]
          | IncomingDocumentSummary[]
          | ClaimInboxItemSummary[]
          | QuoteInboxItemSummary[]
          | BilloutInboxItemSummary[]
          | CaseInboxItemSummary[]
          | { [key: string]: any }[],
        gridLoading: false,
        page: 1,
        pageCount: 0,
        firstRecordIndex: 0,
        lastRecordIndex: 0,
        totalItems: 0,
        bulkActions: new Array<InboxActionSummary>(),
        checkInboxItems: new Array<BulkActionItem>(),
      })
      return
    }

    this.props.setSelectedInboxType!(selectedType)
    this.props.setSelectedInbox!(undefined)

    this.setState(
      {
        selectedType: selectedType,
        gridLoading: true,
        dynamicColumns: new Array<string>(),
        gridData: new pagedList().items as
          | DocumentInboxItemSummary[]
          | IncomingDocumentSummary[]
          | ClaimInboxItemSummary[]
          | QuoteInboxItemSummary[]
          | BilloutInboxItemSummary[]
          | CaseInboxItemSummary[]
          | { [key: string]: any }[],
        page: 1,
        pageCount: 0,
        firstRecordIndex: 0,
        lastRecordIndex: 0,
        totalItems: 0,
        selectedItemType: '',
        inbox: undefined as unknown as InboxSummary,
        order: 'asc',
        orderBy: '',
      },
      () => this.loadInboxes()
    )
  }

  private setSelectedBulkAction = (event: SelectChangeEvent<string>) => {
    let action = new InboxActionSummary()

    if (event.target.value !== undefined) {
      this.state.bulkActions.forEach((element) => {
        if (element.id.toString() === event.target.value) {
          action = element
        }
      })
    }

    this.setState({
      selectedBulkAction: event.target.value === '' ? undefined : event.target.value,
      actionToExecute: action,
    })
  }

  private mapInboxHeaders = () => {
    if (this.state.inbox && !this.state.inbox.inboxType.includes('Dynamic')) {
      return (
        <TableHead>
          <TableRow className="gridPaddingDense">
            {this.state.bulkActions.length > 0 && (
              <TableCell className="checkboxCell"></TableCell>
            )}
            <TableCell className="openButtonCell"></TableCell>{' '}
            {/* "Open" button header cell */}
            {this.state.inbox.itemType == InboxItemType.Quote && <TableCell></TableCell>}
            {this.state.inbox.itemType == InboxItemType.Quote && (
              <TableCell className="openButtonCell"></TableCell>
            )}
            {/* Money tooltip header cell ONLY for Case */}
            {this.state.inbox.itemType == InboxItemType.Case && <TableCell></TableCell>}
            {this.renderSortedHeaderIfNotNewlyAdded('Priority', 'Priority', 'center')}
            {this.renderSortedHeaderIfNotNewlyAdded('Entry Date', 'EntryDate', 'right')}
            {this.state.inbox?.id == -1 && (
              <TableCell align="right">Entry Date</TableCell>
            )}
            {this.state.selectedItemType == InboxItemType.Claim && this.mapClaimHeaders()}
            {this.state.selectedItemType == InboxItemType.Document &&
              this.mapDocumentHeaders()}
            {this.state.selectedItemType == InboxItemType.Quote && this.mapQuoteHeaders()}
            {this.state.selectedItemType == InboxItemType.Billout &&
              this.mapBilloutHeaders()}
            {this.state.selectedItemType == InboxItemType.Case && this.mapCaseHeaders()}
            {this.state.selectedItemType == InboxItemType.Invoice &&
              this.mapInvoiceHeaders()}
            {this.state.inbox?.id == -1 && this.mapNewDocumentHeaders()}
            {this.renderSortedHeaderIfNotNewlyAdded('Assigned To', 'AssignedUser')}
            {this.renderSortedHeaderIfNotNewlyAdded('Pend', 'PendDate', 'right')}
            {this.renderSortedHeaderIfNotNewlyAdded('Lock', 'LockedBy')}
          </TableRow>
        </TableHead>
      )
    } else {
      return (
        <TableHead>
          <TableRow className="gridPaddingDense">
            {this.state.dynamicColumns?.map((column, index) =>
              this.renderDynamicHeaderColumn(column, (index + 1).toString())
            )}
          </TableRow>
        </TableHead>
      )
    }
  }

  public closeModal(actionExecuted: boolean) {
    if (actionExecuted) {
      location.reload()
    }

    this.setState({ activeModal: null, executed: false })
  }

  private handleFilterProviderGroupChange = (event: any) => {
    this.setState({
      filterClaimProviderGroup: event.target.value,
    })
  }

  private handleProviderTypeAbbreviationChange = (event: any) => {
    let value = event.target.value == '' ? undefined : event.target.value
    this.setState({ filterClaimProviderTypeAbbreviation: value })
  }

  private handleReconcileUrgencyChange = (event: any) => {
    let value = event.target.value == '' ? undefined : event.target.value
    this.setState({ filterQuoteReconcileUrgency: value })
  }

  private getSavedInvoiceStatus = () =>
    this.props.savedFixedInboxFilter?.hasInvoice
      ? 'Invoiced'
      : this.props.savedFixedInboxFilter?.notInvoiced
        ? 'NotInvoiced'
        : ''

  private handleInvoiceStatusChange = (event: any) => {
    this.setState({ filterQuoteInvoiceStatus: event.target.value })
  }

  private handleQuoteEocStatusChange = (event: any) => {
    this.setState({ filterQuoteEocStatus: event.target.value })
  }

  private handleQuoteTypesChange = (event: SelectChangeEvent<string[]>) => {
    this.setState({ filterQuoteTypes: event.target.value as string[] })
  }

  private handleInsuranceCompanyChange = (event: any) => {
    let value = event.target.value == '' ? undefined : event.target.value
    this.setState({ filterQuoteInsuranceCompany: value })
  }

  private clearQuoteFilter = (reload: boolean = true) => {
    const select_box = document.getElementById(
      'filterQuoteReconcileUrgency'
    ) as HTMLSelectElement

    if (select_box != undefined) {
      select_box.selectedIndex = 0
    }

    const insuranceCompany = document.getElementById(
      'filterQuoteInsuranceCompany'
    ) as HTMLInputElement

    if (insuranceCompany != undefined) {
      insuranceCompany.value = ''
    }

    this.setState({
      filterQuoteInsuranceCompany: undefined,
      filterQuoteReconcileUrgency: undefined,
      filterQuoteInvoiceStatus: '',
      filterQuoteEocStatus: '',
      filterQuoteTypes: new Array<string>(),
    })

    this.props.setFixedImportRequest!(new FixedInboxSearchRequest())

    if (reload) {
      this.getInboxData()
        .then(() => {
          this.setState({
            checkInboxItems: new Array<BulkActionItem>(),
          })
        })
        .catch(() => {})
    }
  }

  private clearClaimFilter = (reload: boolean = true) => {
    const typaAbbreviationFilter = document.getElementById(
      'filterClaimProviderTypeAbbreviation'
    ) as HTMLSelectElement

    if (typaAbbreviationFilter != undefined) {
      typaAbbreviationFilter.selectedIndex = 0
    }

    const urgency = document.getElementById(
      'filterQuoteReconcileUrgency'
    ) as HTMLSelectElement

    if (urgency != undefined) {
      urgency.selectedIndex = 0
    }

    const claimProviderGroup = document.getElementById(
      'filterClaimProviderGroup'
    ) as HTMLInputElement

    if (claimProviderGroup != undefined) {
      claimProviderGroup.value = ''
    }

    this.setState({
      filterClaimProviderTypeAbbreviation: undefined,
      filterClaimProviderGroup: undefined,
      filterQuoteReconcileUrgency: undefined,
    })

    this.props.setFixedImportRequest!(new FixedInboxSearchRequest())

    if (reload) {
      this.getInboxData()
        .then(() => {
          this.setState({
            checkInboxItems: new Array<BulkActionItem>(),
          })
        })
        .catch(() => {})
    }
  }

  public render() {
    const forcePage =
      this.state.pageCount > 0
        ? Math.min(Math.max(this.state.page - 1, 0), this.state.pageCount - 1)
        : undefined

    return (
      <>
        <BundleClaimsModal />
        {this.props.selectedClaimId && this.props.isAppealLogModalOpen ? (
          <AppealLogModal
            claimId={this.props.selectedClaimId}
            isOpen={this.props.isAppealLogModalOpen}
            closeModal={this.props.closeAppealLogModal!}
            groupInfo={this.state.groupInfo}
          />
        ) : (
          <></>
        )}
        <Helmet>
          <title>Inboxes</title>
        </Helmet>
        <ResponsiveAppBar title={'Inboxes'} pageIcon={<Inbox />} />
        <Dialog
          open={this.state.favoriteModalOpen}
          onClose={this.closeFavoriteModal}
          fullWidth
          maxWidth="md"
        >
          <ResponsiveAppBar title="Manage Favorite Inboxes" isModal={true} />
          <DialogContent>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Favorite</TableCell>
                  <TableCell>Inbox</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {this.state.favoriteInboxes.map((inbox) => (
                  <TableRow key={inbox.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          inbox.inboxType == 'DynamicPersonal' || inbox.isUserFavorite
                        }
                        onChange={() => this.toggleInboxFavorite(inbox.id)}
                        disabled={
                          this.state.favoriteSaving ||
                          inbox.inboxType == 'DynamicPersonal'
                        }
                      />
                    </TableCell>
                    <TableCell>{inbox.inboxName}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={this.closeFavoriteModal}
              disabled={this.state.favoriteSaving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => this.saveInboxFavorites().catch(() => {})}
              disabled={this.state.favoriteSaving}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>
        {this.state.inbox &&
          this.state.activeModal &&
          this.state.executed &&
          this.state.actionToExecute &&
          bulkModalRenderers[this.state.activeModal]({
            onClose: this.closeModal,
            isOpen: this.state.activeModal != undefined,
            inboxItems: this.state.checkInboxItems,
            action:
              this.state.actionToExecute == undefined
                ? new InboxActionSummary()
                : this.state.actionToExecute,
            itemType: this.state.inbox.itemType as InboxItemType,
            inboxId: this.state.inbox.id.toString(),
            userName: this.props.user?.name,
          })}
        {this.state.gridLoading && (
          <div
            style={{
              position: 'fixed',
              display: 'flex',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 2000,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={100} />
          </div>
        )}
        <Grid
          container
          style={{ width: '100%', paddingLeft: '8px', paddingRight: '8px' }}
        >
          <Grid
            item
            style={{
              paddingTop: '4px',
              paddingLeft: '8px',
              paddingBottom: '8px',
            }}
            xs={12}
          >
            <h3>Please select an inbox</h3>
          </Grid>
          <Grid
            item
            container
            xs={12}
            style={{ paddingLeft: '8px', paddingBottom: '20px' }}
            spacing={1}
          >
            <Grid item xs={2}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel shrink={true} variant="outlined">
                  Inbox Type
                </InputLabel>
                <Select
                  variant="outlined"
                  input={<OutlinedInput notched label="Inbox Type" />}
                  native={true}
                  fullWidth
                  onChange={this.setSelectedType}
                  id="type"
                  value={this.state.selectedType ?? ''}
                  disabled={this.availableInboxTypes().length <= 1}
                >
                  {this.renderInboxTypeOptions()}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={5}>
              <FormControl variant="outlined" fullWidth>
                <InputLabel shrink={true} variant="outlined">
                  Inboxes
                </InputLabel>
                <Select
                  variant="outlined"
                  input={<OutlinedInput notched label="Inboxes" />}
                  fullWidth
                  onChange={this.setSelectedInbox}
                  id="inboxSelect"
                  value={this.state.inbox?.id ?? ''}
                  disabled={!this.state.selectedType}
                >
                  {this.state.inboxes?.map((inbox, index) => (
                    <MenuItem
                      key={`inbox-${inbox.id}-${index}`}
                      value={inbox.id}
                      disabled={inbox.inboxType == 'line'}
                    >
                      {inbox.isUserFavorite && (
                        <span
                          style={{
                            fontSize: 'large',
                            verticalAlign: 'top',
                            display: 'inline-flex',
                            alignItems: 'center',
                            cursor: 'default',
                          }}
                        >
                          ⭐
                        </span>
                      )}
                      {inbox.itemCount != undefined ? (
                        <span>
                          {inbox.inboxName} <strong>({inbox.itemCount} items)</strong>
                        </span>
                      ) : (
                        inbox.inboxName
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item>
              <Chip
                clickable
                label="EDIT FAVORITES"
                icon={
                  <span
                    style={{
                      fontSize: 'large',
                      display: 'inline-flex',
                      alignItems: 'center',
                      cursor: 'default',
                    }}
                  >
                    ⭐
                  </span>
                }
                onClick={this.openFavoriteModal}
                disabled={!this.state.selectedType}
                style={{ verticalAlign: 'top' }}
              />
            </Grid>
            {this.renderFavoriteInboxLinks()}
          </Grid>
          {this.state.inbox && (
            <Grid
              item
              style={{
                paddingTop: '0px',
                paddingLeft: '0px',
                paddingBottom: '6px',
              }}
              xs={12}
            >
              {(this.state.bulkActions.length > 0 ||
                this.state.inbox.itemType == InboxItemType.Claim ||
                this.state.inbox.itemType == InboxItemType.Quote ||
                this.state.inbox.itemType == InboxItemType.Invoice) && (
                <Grid
                  container
                  direction={this.state.bulkActions.length > 0 ? 'row-reverse' : 'row'}
                  justifyContent="space-between"
                  alignItems="flex-end"
                  style={{ paddingBottom: '10px' }}
                >
                  {this.state.bulkActions.length > 0 && (
                    <Grid item>
                      <Grid
                        container
                        spacing={1}
                        alignItems="center"
                        sx={{
                          paddingTop: '12px',
                          paddingLeft: '10px',
                          paddingRight: '10px',
                          paddingBottom: '20px',
                          backgroundColor: 'action.hover',
                          boxShadow: '0px -2px 4px -1px rgba(85, 85, 85, 0.2)',
                        }}
                      >
                        <Grid item>
                          <FormControl
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={compactFieldSx}
                          >
                            <InputLabel shrink={true} variant="outlined">
                              Bulk Actions
                            </InputLabel>
                            <Select
                              style={{ minWidth: '300px' }}
                              variant="outlined"
                              input={<OutlinedInput notched label="Bulk Actions" />}
                              native={true}
                              fullWidth
                              onChange={this.setSelectedBulkAction}
                              id="actionSelect"
                            >
                              <option value=""></option>
                              {this.state.bulkActions?.map((bulkAction) => (
                                <option key={bulkAction.id} value={bulkAction.id}>
                                  {bulkAction.actionName}
                                </option>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            sx={compactButtonSx}
                            disabled={
                              this.state.selectedBulkAction === undefined ||
                              this.state.checkInboxItems.length === 0
                            }
                            onClick={() => {
                              this.setState({ executed: true })
                              this.executeBulkAction().catch(() => {})
                            }}
                          >
                            Execute
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  {this.state.inbox.itemType == InboxItemType.Claim && (
                    <Grid item>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault()
                          this.getInboxData(true).catch(() => {})
                        }}
                      >
                        <Grid
                          container
                          spacing={1}
                          alignItems="center"
                          sx={{
                            paddingTop: '12px',
                            paddingLeft: '8px',
                            paddingRight: '10px',
                            paddingBottom: '20px',
                            backgroundColor: 'action.hover',
                            boxShadow: '0px -2px 4px -1px rgba(85, 85, 85, 0.2)',
                          }}
                        >
                          <Grid item>
                            <FormControl
                              variant="outlined"
                              fullWidth
                              size="small"
                              sx={compactFieldSx}
                              style={{ minWidth: '150px' }}
                            >
                              <InputLabel shrink={true} variant="outlined">
                                Claim Type
                              </InputLabel>
                              <Select
                                variant="outlined"
                                input={<OutlinedInput notched label="Claim Type" />}
                                native={true}
                                fullWidth
                                onChange={this.handleProviderTypeAbbreviationChange}
                                value={this.state.filterClaimProviderTypeAbbreviation}
                                id="filterClaimProviderTypeAbbreviation"
                              >
                                <option value=""></option>
                                {Object.values(ProviderTypeAbbreviation).map((abbr) => (
                                  <option key={abbr} value={abbr}>
                                    {ProviderTypeLabels[abbr]}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item>
                            <FormControl
                              variant="outlined"
                              size="small"
                              fullWidth
                              sx={compactFieldSx}
                            >
                              <InputLabel shrink={true} variant="outlined">
                                Pymt Tier
                              </InputLabel>
                              <Select
                                style={{ minWidth: '150px' }}
                                variant="outlined"
                                input={<OutlinedInput notched label="Pymt Tier" />}
                                native={true}
                                fullWidth
                                onChange={this.handleReconcileUrgencyChange}
                                value={this.state.filterQuoteReconcileUrgency}
                                id="filterQuoteReconcileUrgency"
                              >
                                <option value=""></option>
                                {Object.values(ProviderGroupPaymentTier).map((tier) => (
                                  <option key={tier} value={tier}>
                                    {ProviderGroupPaymentTierLabels[tier]}
                                  </option>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item>
                            <FormControl fullWidth style={{ minWidth: '300px' }}>
                              <TextField
                                size="small"
                                sx={compactFieldSx}
                                value={this.state.filterClaimProviderGroup}
                                onChange={this.handleFilterProviderGroupChange}
                                variant="outlined"
                                label="Provider Group"
                                name="filterClaimProviderGroup"
                                id="filterClaimProviderGroup"
                                InputLabelProps={{ shrink: true }}
                              />
                            </FormControl>
                          </Grid>
                          <Grid item>
                            <Button
                              type="submit"
                              variant="contained"
                              color="primary"
                              size="small"
                              sx={compactButtonSx}
                              disabled={
                                this.state.filterClaimProviderGroup === undefined &&
                                this.state.filterClaimProviderTypeAbbreviation ===
                                  undefined &&
                                this.state.filterQuoteReconcileUrgency === undefined
                              }
                            >
                              Filter
                            </Button>
                          </Grid>
                          <Grid item>
                            <Button
                              variant="contained"
                              color="secondary"
                              size="small"
                              sx={compactButtonSx}
                              disabled={
                                this.state.filterClaimProviderGroup === undefined &&
                                this.state.filterClaimProviderTypeAbbreviation ===
                                  undefined &&
                                this.state.filterQuoteReconcileUrgency === undefined
                              }
                              onClick={() => {
                                this.clearClaimFilter()
                              }}
                            >
                              Clear Filter
                            </Button>
                          </Grid>
                        </Grid>
                      </form>
                    </Grid>
                  )}
                  {(this.state.inbox.itemType == InboxItemType.Quote ||
                    this.state.inbox.itemType == InboxItemType.Invoice) && (
                    <Grid item>
                      <form
                        onSubmit={(e) => {
                          e.preventDefault()
                          this.getInboxData(true).catch(() => {})
                        }}
                      >
                        <Grid
                          container
                          direction="column"
                          rowSpacing={1.5}
                          sx={{
                            width: '680px',
                            padding: '8px 10px 12px',
                            backgroundColor: 'action.hover',
                            boxShadow: '0px -2px 4px -1px rgba(85, 85, 85, 0.2)',
                          }}
                        >
                          <Grid item container spacing={1} alignItems="center">
                            <Grid item xs={5}>
                              <FormControl
                                variant="outlined"
                                size="small"
                                fullWidth
                                sx={compactFieldSx}
                              >
                                <InputLabel shrink={true} variant="outlined">
                                  Reconcile Urgency
                                </InputLabel>
                                <Select
                                  variant="outlined"
                                  input={
                                    <OutlinedInput notched label="Reconcile Urgency" />
                                  }
                                  native={true}
                                  fullWidth
                                  onChange={this.handleReconcileUrgencyChange}
                                  value={this.state.filterQuoteReconcileUrgency}
                                  id="filterQuoteReconcileUrgency"
                                >
                                  <option value=""></option>
                                  {Object.values(ProviderGroupPaymentTier).map((tier) => (
                                    <option key={tier} value={tier}>
                                      {ProviderGroupPaymentTierLabels[tier]}
                                    </option>
                                  ))}
                                </Select>
                              </FormControl>
                            </Grid>
                            <Grid item xs={7}>
                              <FormControl fullWidth>
                                <TextField
                                  size="small"
                                  sx={compactFieldSx}
                                  value={this.state.filterQuoteInsuranceCompany}
                                  onChange={this.handleInsuranceCompanyChange}
                                  variant="outlined"
                                  label="Insurance Company"
                                  name="filterQuoteInsuranceCompany"
                                  id="filterQuoteInsuranceCompany"
                                  InputLabelProps={{ shrink: true }}
                                />
                              </FormControl>
                            </Grid>
                          </Grid>
                          <Grid item container spacing={1} alignItems="center">
                            {this.state.inbox.itemType == InboxItemType.Quote && (
                              <Grid item xs={3}>
                                <FormControl
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  sx={compactFieldSx}
                                >
                                  <InputLabel shrink={true} variant="outlined">
                                    Invoice Status
                                  </InputLabel>
                                  <Select
                                    variant="outlined"
                                    input={
                                      <OutlinedInput notched label="Invoice Status" />
                                    }
                                    native={true}
                                    fullWidth
                                    onChange={this.handleInvoiceStatusChange}
                                    value={this.state.filterQuoteInvoiceStatus}
                                    id="filterQuoteInvoiceStatus"
                                  >
                                    <option value=""></option>
                                    <option value="Invoiced">Invoiced</option>
                                    <option value="NotInvoiced">Not Invoiced</option>
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}
                            {this.state.inbox.itemType == InboxItemType.Quote && (
                              <Grid item xs={5}>
                                <FormControl
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  sx={compactFieldSx}
                                >
                                  <InputLabel shrink={true} variant="outlined">
                                    Quote Type
                                  </InputLabel>
                                  <Select
                                    variant="outlined"
                                    input={<OutlinedInput notched label="Quote Type" />}
                                    fullWidth
                                    multiple
                                    id="filterQuoteTypes"
                                    value={this.state.filterQuoteTypes}
                                    renderValue={(selected) =>
                                      selected
                                        .map((key) => QuoteTypeNameConstants[Number(key)])
                                        .join(', ')
                                    }
                                    onChange={this.handleQuoteTypesChange}
                                  >
                                    {Object.keys(QuoteTypeNameConstants).map((key) => (
                                      <MenuItem key={key} value={key}>
                                        {this.state.filterQuoteTypes.includes(key) ? (
                                          <CheckBox
                                            color="secondary"
                                            style={{ paddingRight: '10px' }}
                                          />
                                        ) : (
                                          <CheckBoxOutlineBlank
                                            color="action"
                                            style={{ paddingRight: '10px' }}
                                          />
                                        )}{' '}
                                        {QuoteTypeNameConstants[Number(key)]}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}
                            {this.state.inbox.itemType == InboxItemType.Quote && (
                              <Grid item xs={4}>
                                <FormControl
                                  variant="outlined"
                                  size="small"
                                  fullWidth
                                  sx={compactFieldSx}
                                >
                                  <InputLabel shrink={true} variant="outlined">
                                    EOC Status
                                  </InputLabel>
                                  <Select
                                    variant="outlined"
                                    input={<OutlinedInput notched label="EOC Status" />}
                                    native={true}
                                    fullWidth
                                    onChange={this.handleQuoteEocStatusChange}
                                    value={this.state.filterQuoteEocStatus}
                                    id="filterQuoteEocStatus"
                                  >
                                    <option value=""></option>
                                    {EOCStatusOptions.map((status) => (
                                      <option key={status} value={status}>
                                        {status}
                                      </option>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                            )}
                            <Grid
                              item
                              xs
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="small"
                                sx={compactButtonSx}
                                disabled={
                                  this.state.filterQuoteReconcileUrgency === undefined &&
                                  this.state.filterQuoteInsuranceCompany === undefined &&
                                  this.state.filterQuoteInvoiceStatus === '' &&
                                  this.state.filterQuoteEocStatus === '' &&
                                  this.state.filterQuoteTypes.length === 0
                                }
                              >
                                Filter
                              </Button>
                              <Button
                                variant="contained"
                                color="secondary"
                                size="small"
                                sx={compactButtonSx}
                                disabled={
                                  this.state.filterQuoteReconcileUrgency === undefined &&
                                  this.state.filterQuoteInsuranceCompany === undefined &&
                                  this.state.filterQuoteInvoiceStatus === '' &&
                                  this.state.filterQuoteEocStatus === '' &&
                                  this.state.filterQuoteTypes.length === 0
                                }
                                onClick={() => {
                                  this.clearQuoteFilter()
                                }}
                              >
                                Clear Filter
                              </Button>
                            </Grid>
                          </Grid>
                        </Grid>
                      </form>
                    </Grid>
                  )}
                </Grid>
              )}
              <Grid item xs={12}>
                <Table
                  key="inboxGrid"
                  aria-label="simple table"
                  size="small"
                  sx={{
                    ...sortedTableHeaderSx,
                    '& .checkboxCell': {
                      paddingLeft: '2px',
                      paddingRight: '2px',
                      width: '1%',
                    },
                    '& .checkboxCell .MuiCheckbox-root': { padding: '4px' },
                    '& .openButtonCell': {
                      paddingLeft: '4px',
                      paddingRight: '4px',
                      width: '1%',
                    },
                  }}
                >
                  {this.mapInboxHeaders()}
                  <TableBody>{this.mapGridData()}</TableBody>
                </Table>
                <div className="pagination-row">
                  {this.paginationInfo()}
                  <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    forcePage={forcePage}
                    onPageChange={this.handlePageChange}
                    pageCount={this.state.pageCount}
                    containerClassName={'pagination'}
                    activeClassName={'active'}
                    //@ts-ignore
                    renderOnZeroPageCount={null}
                  />
                </div>
              </Grid>
            </Grid>
          )}
        </Grid>
      </>
    )
  }
}

const InjectedInboxes = inject<IStores, IInboxesProps, Partial<IInboxesProps>, any>(
  (stores: IStores) => ({
    billStatusFilter: stores.patientEdit.billStatusFilter,
    editBill: stores.patientEdit.enterClaimFromMenu,
    episodeOfCare: stores.patientEdit.selectedEpisodeOfCare,
    isLoading: stores.patientEdit.isLoading,
    setClaimsModalLoadFunction: (parentFunction?: (episodeOfCareId: string) => void) => {
      if (parentFunction) {
        stores.patientEdit.setClaimsModalLoad(parentFunction)
      }
    },
    scheduleId: stores.schedules.selectedScheduleId,
    rerouteToPath: stores.global.rerouteToPath,
    setSelectedInbox: stores.global.setSelectedInbox,
    savedInbox: stores.global.selectedInbox,
    savedOrder: stores.global.selectedInboxOrder,
    savedOrderBy: stores.global.selectedInboxOrderColumn,
    setSavedSort: stores.global.setSelectedInboxSort,
    openBillsDialog: stores.quotes.openBillsDialog,
    savedInboxType: stores.global.selectedInboxType,
    setSelectedInboxType: stores.global.setSelectedInboxType,
    organizationId: stores.global.currentAppOrganization?.id as OrganizationId,
    setFixedImportRequest: stores.global.setFixedImportRequest,
    savedFixedInboxFilter: stores.global.savedFixedInboxFilter,
    user: stores.global.user,
    openAppealLogModal: stores.cms1500.OpenAppealLogModal,
    closeAppealLogModal: stores.cms1500.closeAppealLogModal,
    isAppealLogModalOpen: stores.cms1500.isAppealLogModalOpen,
    selectedClaimId: stores.cms1500.selectedClaimId,
  })
)(observer(Inboxes))

export default InjectedInboxes
